import React, { Component } from 'react';
import { withStyles } from '@material-ui/core/styles';
import Grid from '@material-ui/core/Grid';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import CardActions from '@material-ui/core/CardActions';
import Typography from '@material-ui/core/Typography';
import Chip from '@material-ui/core/Chip';
import Button from '@material-ui/core/Button';
import Tabs from '@material-ui/core/Tabs';
import Tab from '@material-ui/core/Tab';
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import MovieIcon from '@material-ui/icons/Movie';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';

import { Markdown } from '../../../common/components/Markdown';
import { getProject } from '../../../common/services/project';
import {
  UI_TEXTS,
  VIDEO_TEMPLATE_FORMATS,
  VIDEO_TEMPLATE_LANGS,
  demoPosterPath,
  demoVideoPath,
  helpDocPath,
} from './formats';

const styles = theme => ({
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
  },
  intro: {
    margin: theme.spacing(1, 0, 3),
  },
  card: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
  },
  mediaWrapper: {
    position: 'relative',
    paddingTop: '56.25%', // 16/9
    backgroundColor: theme.palette.grey[200],
  },
  media: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    objectFit: 'contain',
    backgroundColor: '#000',
  },
  placeholder: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    color: theme.palette.grey[500],
  },
  placeholderIcon: {
    fontSize: 48,
    marginBottom: theme.spacing(1),
  },
  content: {
    flex: 1,
  },
  title: {
    fontWeight: 600,
  },
  chips: {
    marginTop: theme.spacing(1),
    '& > *': {
      marginRight: theme.spacing(0.5),
    },
  },
  langTab: {
    minWidth: 56,
  },
});

@withStyles(styles)
export default class VideoTemplates extends Component {
  state = {
    // Langue du site par défaut (FR sur Foot Mercato, ES sur Fichajes, DE sur Fussball Transfers) ;
    // l'onglet EN sert aux versions anglaises (pas de site EN dans footWiki).
    lang: VIDEO_TEMPLATE_LANGS.includes(getProject().lang) ? getProject().lang : 'fr',
    helpFormat: null,
  };

  changeLang = (event, lang) => this.setState({ lang });

  openHelp = format => this.setState({ helpFormat: format });

  closeHelp = () => this.setState({ helpFormat: null });

  renderMedia(format, texts) {
    const { classes } = this.props;
    const { lang } = this.state;

    if (format.demoLangs && format.demoLangs.includes(lang)) {
      return (
        <video
          key={`${format.id}-${lang}`}
          className={classes.media}
          controls
          preload="none"
          poster={demoPosterPath(lang, format.id)}
        >
          <source src={demoVideoPath(lang, format.id)} type="video/mp4" />
        </video>
      );
    }

    return (
      <div className={classes.placeholder}>
        <MovieIcon className={classes.placeholderIcon} />
        <Typography variant="body2">{texts.demoSoon}</Typography>
      </div>
    );
  }

  renderCard(format, texts) {
    const { classes } = this.props;
    const { lang } = this.state;

    return (
      <Grid item xs={12} sm={6} lg={4} key={format.id}>
        <Card variant="outlined" className={classes.card}>
          <div className={classes.mediaWrapper}>{this.renderMedia(format, texts)}</div>
          <CardContent className={classes.content}>
            <Typography variant="subtitle1" className={classes.title}>
              {format.label[lang]}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {format.description[lang]}
            </Typography>
            <div className={classes.chips}>
              <Chip size="small" label={format.ratio} />
              <Chip size="small" label={format.duration[lang]} />
            </div>
          </CardContent>
          <CardActions>
            <Button size="small" color="primary" startIcon={<InfoOutlinedIcon />} onClick={() => this.openHelp(format)}>
              {texts.learnMore}
            </Button>
          </CardActions>
        </Card>
      </Grid>
    );
  }

  render() {
    const { classes } = this.props;
    const { lang, helpFormat } = this.state;
    const texts = UI_TEXTS[lang];

    return (
      <div>
        <div className={classes.header}>
          <Typography variant="h5">{texts.title}</Typography>
          <Tabs value={lang} onChange={this.changeLang} indicatorColor="primary" textColor="primary">
            {VIDEO_TEMPLATE_LANGS.map(l => (
              <Tab key={l} value={l} label={l.toUpperCase()} className={classes.langTab} />
            ))}
          </Tabs>
        </div>
        <Typography variant="body1" className={classes.intro}>
          {texts.intro}
        </Typography>
        <Grid container spacing={2}>
          {VIDEO_TEMPLATE_FORMATS.map(format => this.renderCard(format, texts))}
        </Grid>
        <Dialog open={!!helpFormat} onClose={this.closeHelp} maxWidth="md" fullWidth>
          {helpFormat && <DialogTitle>{helpFormat.label[lang]}</DialogTitle>}
          <DialogContent dividers>
            {helpFormat && <Markdown key={`${lang}-${helpFormat.id}`} filePath={helpDocPath(lang, helpFormat.id)} />}
          </DialogContent>
          <DialogActions>
            <Button onClick={this.closeHelp} color="primary">
              {texts.close}
            </Button>
          </DialogActions>
        </Dialog>
      </div>
    );
  }
}
